<template>
    <div class="selected-filters" v-if="hasActiveFilters">
        <div class="selected-filters-header">
            <h4 class="selected-filters-title">{{ $t('selected-filters') }}</h4>
        <div class="selected-filter selected-filter--clear" @click="clearAllFilters" v-if="activeFilters.length > 1">
            <span class="selected-filter-name">{{ $t('clear-all-filters') }}</span>
        </div>
        </div>
        <div class="selected-filters-container">
            <div class="selected-filter" v-for="filter in activeFilters" :key="filter.slug">
                <span class="selected-filter-name">{{ filter.name }}</span>
                <span class="selected-filter-remove" @click="removeFilter(filter)">×</span>
            </div>
            
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useProductStore } from "@/store/products";
import { useFileTypeStore } from "@/store/filetypes";
import { useMarketingMaterialStore } from "@/store/marketingMaterial";
import { useBySpacesStore } from "@/store/bySpaces";
import filters from '@/helpers/filters';

const router = useRouter();
const route = useRoute();

const productStore = useProductStore();
const fileTypeStore = useFileTypeStore();
const marketingMaterialStore = useMarketingMaterialStore();
const bySpacesStore = useBySpacesStore();

// Obtener los filtros activos desde los parámetros de la ruta
const activeFilterParams = computed(() => {
    const params = route.params;
    const activeFilters = {};
    
    if (params.categories) activeFilters.categories = params.categories;
    if (params.fileTypes) activeFilters.fileTypes = params.fileTypes;
    if (params.bySpaces) activeFilters.bySpaces = params.bySpaces;
    if (params.marketingMaterials) activeFilters.marketingMaterials = params.marketingMaterials;
    
    return activeFilters;
});

// Comprobar si hay filtros activos
const hasActiveFilters = computed(() => {
    return Object.values(activeFilterParams.value).some(filterGroup => filterGroup && filterGroup.length > 0);
});

// Convertir los slugs de los filtros activos a objetos con nombre y tipo
const activeFilters = computed(() => {
    const result = [];
    
    if (activeFilterParams.value.categories) {
        activeFilterParams.value.categories.forEach(slug => {
            const product = productStore.getProductBySlug(slug);
            if (product) {
                result.push({
                    slug,
                    name: product.name,
                    type: 'categories'
                });
            }
        });
    }
    
    if (activeFilterParams.value.fileTypes) {
        activeFilterParams.value.fileTypes.forEach(slug => {
            const fileType = fileTypeStore.getFileTypeBySlug(slug);
            if (fileType) {
                result.push({
                    slug,
                    name: fileType.name,
                    type: 'fileTypes'
                });
            }
        });
    }
    
    if (activeFilterParams.value.bySpaces) {
        activeFilterParams.value.bySpaces.forEach(slug => {
            const space = bySpacesStore.bySpaces.find(s => s.slug === slug);
            if (space) {
                result.push({
                    slug,
                    name: space.name,
                    type: 'bySpaces'
                });
            }
        });
    }
    
    if (activeFilterParams.value.marketingMaterials) {
        activeFilterParams.value.marketingMaterials.forEach(slug => {
            const material = marketingMaterialStore.marketingMaterials.find(m => m.slug === slug);
            if (material) {
                result.push({
                    slug,
                    name: material.name,
                    type: 'marketingMaterials'
                });
            }
        });
    }
    
    return result;
});

// Función para eliminar un filtro individual
const removeFilter = (filter) => {
    const params = structuredClone(route.params);
    const index = params[filter.type]?.indexOf(filter.slug);
    
    if (index > -1) {
        params[filter.type].splice(index, 1);
        if (params[filter.type].length === 0) {
            delete params[filter.type];
        }
        updateRoute(params);
    }
};

// Función para eliminar todos los filtros
const clearAllFilters = () => {
    const params = structuredClone(route.params);
    
    // Eliminar todos los parámetros de filtro
    delete params.categories;
    delete params.fileTypes;
    delete params.bySpaces;
    delete params.marketingMaterials;
    
    updateRoute(params);
};

// Función para actualizar la ruta con los nuevos parámetros
const updateRoute = (params) => {
    let path = '';
    const isModels = route.path === '/' || route.path.startsWith('/models');
    const arr = ['gallery-images', 'galeria', 'white-image', 'blanco', 'environmental-image', 'entorno'];
    
    if (isModels && params.fileTypes && params.fileTypes.length > 0 && arr.includes(params.fileTypes[0])) {
        path = '/models/file-types/' + params.fileTypes.join('/');
    } else {
        const sortedFilters = ['categories', 'fileTypes', 'marketingMaterials', 'bySpaces'];
        const keys = Object.keys(params).sort((a, b) => sortedFilters.indexOf(a) - sortedFilters.indexOf(b));
        
        const allowedParams = ['categories', 'fileTypes', 'marketingMaterials', 'bySpaces'];
        
        for (let param of keys) {
            if (params[param]?.length > 0 && allowedParams.includes(param)) {
                path += `/${filters.camelToKebabCase(param)}/${params[param].join('/')}`;
            }
        }
    }
    
    if (path === '') path = '/';
    
    router.replace({ path });
};
</script>

<style scoped lang="scss">
.selected-filters {
    border-top: 0.5px solid rgba(96, 96, 96, 0.7);
    padding: 1.25rem 0rem;

    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.25rem;
    }
    
    &-title {
        font-family: 'Tw Cen MT';
        font-weight: 300;
        font-size: 0.875rem;
        line-height: 1.75rem;
        color: $mid-grey-color;
        text-transform: uppercase;
        margin: 0;
    }
    
    &-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
}

.selected-filter {
    display: flex;
    align-items: center;
    border: 0.5px solid rgba(96, 96, 96, 0.7);
    padding: 0.25rem 0.75rem;
    font-size: 0.8rem;
    font-family: 'Tw Cen MT';
    line-height: 1.5;
    gap: 0.5rem;
    cursor: pointer;
    
    &-name {
        white-space: nowrap;
    }
    
    &-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        font-weight: bold;
    }
    
    &:hover {
        background-color: $corporate-light-color;
    }
    
    &--clear {
        background-color: $mid-grey-color;
        color: white;
        
        &:hover {
            background-color: darken($mid-grey-color, 10%);
        }
    }
}
</style>
